import React, { PropTypes } from "react";
import { Link } from "react-router";
import { FormattedMessage } from "react-intl";

import { MANUAL, RECURRING } from "../enums/billingPeriodTypes";
import {
  INFORMATION_AGENT,
  PLAN_ADMINISTRATOR,
  AGENT,
  MANAGER,
  FINANCE,
  DIRECTOR,
  ADMINISTRATOR,
  FINTECH,
} from "../../../../utils/Roles";
import userHasAccess from "../../../../utils/userHasAccess";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../enums/clientStatusTypes";

function renderMenu(menu, clientBillingPeriodType, shouldRender) {
  const hasRoleInList = userHasAccess(menu.requiredRole);
  const isExactRole = !!menu.exactRole
    ? userHasAccess(menu.exactRole, true)
    : false;

  return (
    (hasRoleInList || isExactRole) &&
    (!menu.disabledBillingPeriodType ||
      menu.disabledBillingPeriodType.value !== clientBillingPeriodType) &&
    shouldRender
  );
}

const isCorporateUser = (client) => {
  const associatedClientId = localStorage.getItem("associatedClientId");
  if (!!associatedClientId && associatedClientId !== "null") {
    return client.clientBillingPeriodType === RECURRING.value;
  }
  return true;
};

const getRequiredRoleForClass = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
      return PLAN_ADMINISTRATOR;
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return AGENT;
  }
};

const getRequiredRoleForClientCoverages = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
      return FINANCE;
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return AGENT;
  }
};

const getExactRoleForBulkSalary = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
      return [ADMINISTRATOR, FINTECH, MANAGER, FINANCE, DIRECTOR, PLAN_ADMINISTRATOR];
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return [ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, PLAN_ADMINISTRATOR];
  }
};

const getRequiredRoleForClientUsers = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return MANAGER;
  }
};

const getRequiredRoleForInvoices = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
      return PLAN_ADMINISTRATOR;
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return MANAGER;
  }
};

const getExactRoleForInvoices = (client) => {
  switch (client.clientStatusType) {
    case IMPLEMENTATION.value:
      return [PLAN_ADMINISTRATOR];
    case ACTIVE.value:
    case INACTIVE.value:
    case TERMINATED.value:
      return [PLAN_ADMINISTRATOR, INFORMATION_AGENT];
  }
};

const ClientNav = (props) => {
  const menus = [
    {
      requiredRole: AGENT,
      exactRole: [INFORMATION_AGENT, PLAN_ADMINISTRATOR],
      to: (clientId) => `/dashboard/clients/${clientId}/members`,
      title: "clients.client.pages.members",
    },
    {
      requiredRole: AGENT,
      exactRole: [INFORMATION_AGENT, PLAN_ADMINISTRATOR],
      to: (clientId) => `/dashboard/clients/${clientId}/divisions`,
      title: "clients.client.pages.divisions",
    },
    {
      requiredRole: getRequiredRoleForClass(props.client),
      to: (clientId) => `/dashboard/clients/${clientId}/classes`,
      title: "clients.client.pages.classes",
    },
    {
      requiredRole: AGENT,
      exactRole: [INFORMATION_AGENT, PLAN_ADMINISTRATOR],
      to: (clientId) => `/dashboard/clients/${clientId}/billingPeriods`,
      title: "clients.client.pages.billingPeriods",
    },
    {
      requiredRole: getRequiredRoleForClientCoverages(props.client),
      to: (clientId) => `/dashboard/clients/${clientId}/client-coverages`,
      title: "clients.client.pages.coverages",
    },
    {
      requiredRole: getRequiredRoleForInvoices(props.client),
      exactRole: getExactRoleForInvoices(props.client),
      to: (clientId) => `/dashboard/clients/${clientId}/invoices`,
      title: "clients.client.pages.invoices",
    },
    {
      requiredRole: AGENT,
      disabledBillingPeriodType: RECURRING,
      shouldRender: (client) => !!client.shouldImportWithoutId,
      to: (clientId) => `/dashboard/clients/${clientId}/member-identifier`,
      title: "clients.client.pages.memberIdentifier",
    },
    {
      requiredRole: MANAGER,
      exactRole: [PLAN_ADMINISTRATOR],
      shouldRender: isCorporateUser,
      to: (clientId) => `/dashboard/clients/${clientId}/reports`,
      title: "clients.client.pages.reports",
    },
    {
      requiredRole: AGENT,
      exactRole: [INFORMATION_AGENT, PLAN_ADMINISTRATOR],
      to: (clientId) => `/dashboard/clients/${clientId}/client-attachments`,
      title: "clients.client.pages.clientAttachments",
      count: props.clientAttachmentsCount,
    },
    {
      disabledBillingPeriodType: MANUAL,
      exactRole: getExactRoleForBulkSalary(props.client),
      to: (clientId) => `/dashboard/clients/${clientId}/bulk-salaries`,
      title: "clients.client.pages.bulkSalary",
    },
    {
      requiredRole: AGENT,
      exactRole: [PLAN_ADMINISTRATOR],
      disabledBillingPeriodType: MANUAL,
      to: (clientId) => `/dashboard/clients/${clientId}/premium-simulator`,
      title: "clients.client.pages.premiumSimulator",
    },
    {
      requiredRole: getRequiredRoleForClientUsers(props.client),
      to: (clientId) => `/dashboard/clients/${clientId}/users`,
      title: "clients.client.pages.clientUsers",
    },
    {
      requiredRole: MANAGER,
      to: (clientId) => `/dashboard/clients/${clientId}/configurations`,
      title: "clients.client.pages.configurations",
    },
    {
      exactRole: [ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, FINANCE],
      disabledBillingPeriodType: RECURRING,
      to: (clientId) => `/dashboard/clients/${clientId}/synchronizations`,
      title: "clients.client.pages.synchronizations",
    },
  ];

  return (
    <div className="sub-nav">
      <ul>
        {menus
          .filter((menu) =>
            renderMenu(
              menu,
              props.clientBillingPeriodType,
              menu.shouldRender ? menu.shouldRender(props.client) : () => true
            )
          )
          .map((menu, index) => {
            return (
              <li key={index}>
                <Link to={menu.to(props.clientId)} activeClassName="active">
                  <FormattedMessage id={menu.title} />
                  {!!menu.count && <span>({menu.count})</span>}
                </Link>
              </li>
            );
          })}
      </ul>
    </div>
  );
};

ClientNav.propTypes = {
  client: PropTypes.object,
  clientAttachmentsCount: PropTypes.number,
  clientBillingPeriodType: PropTypes.string,
  clientId: PropTypes.number,
};

export default ClientNav;



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/ClientNav.js